import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ko';

// Configure dayjs
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('ko');
dayjs.tz.setDefault('Asia/Seoul');

/**
 * Format date/time in Korean standard (24-hour format, KST)
 */
export const formatDateTime = (date: Date | string): string => {
  return dayjs(date).tz('Asia/Seoul').format('YYYY년 MM월 DD일 HH:mm');
};

export const formatDate = (date: Date | string): string => {
  return dayjs(date).tz('Asia/Seoul').format('YYYY년 MM월 DD일');
};

export const formatTime = (date: Date | string): string => {
  return dayjs(date).tz('Asia/Seoul').format('HH:mm:ss');
};

/**
 * Format time ago in Korean
 */
export const formatTimeAgo = (date: Date | string): string => {
  const now = dayjs();
  const target = dayjs(date);
  const diffMinutes = now.diff(target, 'minute');
  const diffHours = now.diff(target, 'hour');
  const diffDays = now.diff(target, 'day');

  if (diffMinutes < 60) {
    return `${diffMinutes}분 전`;
  } else if (diffHours < 24) {
    return `${diffHours}시간 전`;
  } else {
    return `${diffDays}일 전`;
  }
};

/**
 * Format vital signs with proper units
 */
export const formatVital = (value: number, unit: string): string => {
  const formatter = new Intl.NumberFormat('ko-KR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

  return `${formatter.format(value)} ${unit}`;
};

/**
 * Format percentage
 */
export const formatPercent = (value: number): string => {
  const formatter = new Intl.NumberFormat('ko-KR', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return formatter.format(value / 100);
};

/**
 * Get risk status label in Korean
 */
export const getRiskStatusLabel = (status: string): string => {
  const statusMap: Record<string, string> = {
    'alert': '경보',
    'watch': '주의',
    'stable': '안정',
    'critical': '위험',
    'high': '높음',
    'medium': '중간',
    'low': '낮음'
  };

  return statusMap[status.toLowerCase()] || status;
};

/**
 * Get medical term in Korean
 */
export const getMedicalTerm = (term: string): string => {
  const termMap: Record<string, string> = {
    'SBP': '수축기혈압',
    'DBP': '이완기혈압',
    'MAP': '평균동맥압',
    'HR': '심박수',
    'RR': '호흡수',
    'SpO2': '산소포화도',
    'BT': '체온',
    'respiratory_depression': '호흡 억제',
    'hypotension': '저혈압',
    'sedative': '진정제',
    'hypnotic': '수면제'
  };

  return termMap[term] || term;
};